import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../../store/useAppStore';
import { useAppContext } from '../../hooks/useAppContext';
import {
    Users,
    Building2,
    FileText,
    Download,
    Clock,
    UserPlus,
    Users2,
    LayoutDashboard,
    ArrowRight,
    Activity,
    Zap
} from 'lucide-react';

// Helper function for precise time ago formatting
const timeAgo = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 30) return `منذ بضع ثوان`;

    let interval = seconds / 31536000;
    if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
    interval = seconds / 2592000;
    if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
    interval = seconds / 86400;
    if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
    interval = seconds / 3600;
    if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
    interval = seconds / 60;
    if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
    return `منذ ${Math.floor(seconds)} ثوان`;
};

const ModernComponentsShowcaseScreen: React.FC = () => {
    const { t } = useAppContext();
    const navigate = useNavigate();
    const { users, branches, reports, technicalTeams } = useAppStore();

    const stats = [
        { title: t('totalEmployees'), value: users.length, icon: Users, color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
        { title: t('totalBranches'), value: branches.length, icon: Building2, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
        { title: 'الفرق الفنية', value: technicalTeams.length, icon: Users2, color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
        { title: 'إجمالي التقارير', value: reports.length, icon: FileText, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    ];

    const actions = [
        { title: t('manageEmployees'), path: '/employees', icon: Users, color: 'text-sky-400', bg: 'hover:bg-sky-500/10', border: 'hover:border-sky-500/20' },
        { title: t('manageBranches'), path: '/branches', icon: Building2, color: 'text-amber-400', bg: 'hover:bg-amber-500/10', border: 'hover:border-amber-500/20' },
        { title: 'إدارة الفرق الفنية', path: '/teams', icon: Users2, color: 'text-violet-400', bg: 'hover:bg-violet-500/10', border: 'hover:border-violet-500/20' },
        { title: t('importExport'), path: '/workflow', icon: Download, color: 'text-pink-400', bg: 'hover:bg-pink-500/10', border: 'hover:border-pink-500/20' },
    ];

    const recentActivity = useMemo(() => {
        const recentReports = reports
            .slice(0, 5)
            .map(report => ({
                id: report.id,
                date: report.date,
                type: 'report',
                text: `${report.employeeName} قدم تقرير ${report.type} جديد.`,
            }));

        const recentUsers = users
            .slice(0, 5)
            .map(user => ({
                id: user.id,
                date: user.joinDate,
                type: 'user',
                text: `تمت إضافة موظف جديد: ${user.name}.`,
            }));

        return [...recentReports, ...recentUsers]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 5);
    }, [reports, users]);


    return (
        <div className="space-y-8 animate-contentFadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text flex items-center gap-3">
                        <LayoutDashboard className="text-cyan-400" size={32} />
                        المركز الإداري
                    </h1>
                    <p className="text-slate-400 mt-1">نقطة انطلاقك لإدارة النظام والإشراف عليه</p>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map(({ title, value, icon: Icon, color, bg, border }, idx) => (
                    <div
                        key={title}
                        className={`glass-card p-6 flex items-center justify-between group hover:-translate-y-1 transition-transform duration-300 animate-fadeInUp`}
                        style={{ animationDelay: `${idx * 100}ms` }}
                    >
                        <div>
                            <p className="text-sm font-medium text-slate-400 mb-1">{title}</p>
                            <p className="text-3xl font-bold text-white">{value}</p>
                        </div>
                        <div className={`p-4 rounded-xl ${bg} ${border} border group-hover:scale-110 transition-transform`}>
                            <Icon className={color} size={24} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Quick Actions */}
            <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '400ms' }}>
                <div className="flex items-center gap-2 mb-6">
                    <Zap className="text-amber-400" size={20} />
                    <h3 className="text-lg font-bold text-white">الإجراءات السريعة</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {actions.map(({ title, path, icon: Icon, color, bg, border }, idx) => (
                        <button
                            key={path}
                            onClick={() => navigate(path)}
                            className={`
                                flex flex-col items-center justify-center h-32 gap-3 text-center rounded-xl border border-white/5 bg-white/5 
                                transition-all duration-300 group ${bg} ${border}
                            `}
                        >
                            <div className={`p-3 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors`}>
                                <Icon className={`${color} group-hover:scale-110 transition-transform`} size={28} />
                            </div>
                            <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">{title}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="glass-card p-6 animate-fadeInUp" style={{ animationDelay: '500ms' }}>
                <div className="flex items-center gap-2 mb-6">
                    <Activity className="text-emerald-400" size={20} />
                    <h3 className="text-lg font-bold text-white">آخر النشاطات</h3>
                </div>
                <div className="space-y-4">
                    {recentActivity.length > 0 ? recentActivity.map((item, idx) => (
                        <div
                            key={item.id}
                            className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors group"
                            style={{ animationDelay: `${idx * 100}ms` }}
                        >
                            <div className={`p-3 rounded-full ${item.type === 'report' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                                {item.type === 'report' ? <FileText size={20} /> : <UserPlus size={20} />}
                            </div>
                            <div className="flex-1">
                                <p className="text-slate-200 text-sm font-medium group-hover:text-white transition-colors">{item.text}</p>
                                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                    <Clock size={12} />
                                    {timeAgo(item.date)}
                                </p>
                            </div>
                            <ArrowRight size={16} className="text-slate-600 group-hover:text-cyan-400 transition-colors opacity-0 group-hover:opacity-100" />
                        </div>
                    )) : (
                        <div className="text-center py-8 text-slate-500">
                            لا توجد أنشطة حديثة.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModernComponentsShowcaseScreen;
